import { useMemo } from 'react';
import { View } from 'react-native';

import { useGiftCardContext } from '@fhs/payments';
import { formatCentsToDollarString } from '@fhs/payments/src/utils/helpers';
import { Text } from '@fhs/ui';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import {
  PAYMENT_ENTRY_ADD_NEW_CARD,
  PAYMENT_ENTRY_ADD_NEW_GIFT_CARD,
  PAYMENT_ENTRY_APPLE_PAY,
  PAYMENT_ENTRY_GIFT_CARD,
  PAYMENT_ENTRY_GOOGLE_PAY,
} from '@fhs-legacy/frontend/src/utils/test-ids';

import { ISavedCardInfo } from '../../../../payment';
import { usePaymentMethodStore } from '../../../../state/payment-stores';
import { logAddNewPaymentCard, logPaymentMethodSelected } from '../../../../utils/logging-utils';
import { paymentMethodLabelAndIcon } from '../payment-method-icon-map';
import SavedCardEntry from '../saved-card-entry';

import PaymentEntry from './payment-entry';
import useAvailablePaymentMethods from './use-available-payment-methods';

type Props = {
  onClose(): void;
  onAddNewGiftCardPress?: () => void;
};

export function PaymentMethodSelection({ onClose, onAddNewGiftCardPress }: Props) {
  const selectedPaymentMethod = usePaymentMethodStore(state => state.selectedPaymentMethod);
  const setSelectedPaymentMethod = usePaymentMethodStore(state => state.setSelectedPaymentMethod);
  const setSelectedGiftCardAccountId = usePaymentMethodStore(
    state => state.setSelectedGiftCardAccountId
  );
  const setShowAddPaymentMethod = usePaymentMethodStore(state => state.setShowAddPaymentMethod);
  const setSelectedSavedCard = usePaymentMethodStore(state => state.setSelectedSavedCard);
  const currentSavedCardData = usePaymentMethodStore(state => state.savedCardData);
  const selectedGiftCardAccountId = usePaymentMethodStore(state => state.selectedGiftCardAccountId);
  const showAddPaymentMethod = usePaymentMethodStore(state => state.showAddPaymentMethod);
  const userAccountsData = usePaymentMethodStore(state => state.userAccountsData);
  const { canUseGiftCards, canUseApplePay, canUseGooglePay, userAccountsLoading, isUserAccountsPending } =
    useAvailablePaymentMethods();
  const { logRBIEvent } = useCRMEventsContext();

  const { currentBalance: giftCardBalance } = useGiftCardContext();
  const savedCards = useMemo(() => {
    return (
      userAccountsData.userAccounts?.accounts
        ?.filter(userAccount => userAccount?.credit?.__typename === 'CreditCard')
        .map(userAccount => {
          const savedCardInfo: ISavedCardInfo = {
            ...(userAccount?.credit as ISavedCardInfo),
            accountIdentifier: userAccount?.accountIdentifier || (userAccount as any).fdAccountId,
          };
          return savedCardInfo;
        }) || []
    );
  }, [userAccountsData]);

  const listGiftCardAccounts = useMemo(() => {
    return userAccountsData.userAccounts?.accounts?.filter(account => !!account?.giftCard) || [];
  }, [userAccountsData]);
  const hasExistingGiftCard = listGiftCardAccounts.length > 0;

  const paymentUi = useMemo(
    () => ({
      googlePay: paymentMethodLabelAndIcon.googlePay,
      applePay: paymentMethodLabelAndIcon.applePay,
      giftCard: paymentMethodLabelAndIcon.giftCard,
      addNewGiftCard: paymentMethodLabelAndIcon.addNewGiftCard,
      addNewCard: paymentMethodLabelAndIcon.null,
    }),
    []
  );

  return (
    <>
      {canUseGooglePay && (
        <PaymentEntry
          testID={PAYMENT_ENTRY_GOOGLE_PAY}
          icon={paymentUi.googlePay.icon}
          text={paymentUi.googlePay.label}
          onPress={() => {
            setShowAddPaymentMethod(false);
            setSelectedPaymentMethod('googlePay');
            onClose();
          }}
          selected={selectedPaymentMethod === 'googlePay'}
        />
      )}

      {canUseApplePay && (
        <PaymentEntry
          testID={PAYMENT_ENTRY_APPLE_PAY}
          icon={paymentUi.applePay.icon}
          text={paymentUi.applePay.label}
          onPress={() => {
            logPaymentMethodSelected(logRBIEvent, 'Apple Pay');
            setShowAddPaymentMethod(false);
            setSelectedPaymentMethod('applePay');
            onClose();
          }}
          selected={selectedPaymentMethod === 'applePay'}
        />
      )}

      {isUserAccountsPending && (
        <SavedCardEntry
          loading
          selectedPaymentMethod={selectedPaymentMethod}
          setShowAddPaymentMethod={setShowAddPaymentMethod}
          currentSavedCardData={currentSavedCardData}
          setSelectedSavedCard={setSelectedSavedCard}
          onClose={onClose}
        />
      )}

      {!isUserAccountsPending &&
        savedCards.map(savedCardInfo => (
          <SavedCardEntry
            key={savedCardInfo.accountIdentifier}
            loading={userAccountsLoading}
            savedCardInfo={savedCardInfo}
            selectedPaymentMethod={selectedPaymentMethod}
            setShowAddPaymentMethod={setShowAddPaymentMethod}
            currentSavedCardData={currentSavedCardData}
            setSelectedSavedCard={setSelectedSavedCard}
            onClose={onClose}
          />
        ))}

      {canUseGiftCards &&
        hasExistingGiftCard &&
        listGiftCardAccounts.map(account => {
          const accountId = account?.accountIdentifier ?? '';
          const firstGiftCardId = listGiftCardAccounts[0]?.accountIdentifier ?? '';
          const isSelected =
            selectedPaymentMethod === 'giftCard' &&
            (selectedGiftCardAccountId
              ? selectedGiftCardAccountId === accountId
              : accountId === firstGiftCardId);
          // Use balance from context (single source of truth)
          const displayBalance = giftCardBalance;
          return (
            <PaymentEntry
              key={accountId}
              testID={PAYMENT_ENTRY_GIFT_CARD}
              icon={paymentUi.giftCard.icon}
              text={
                <View>
                  <Text.Ui weight="semibold" size="lg">
                    Gift Card **** {account?.giftCard?.last4}
                  </Text.Ui>
                  <Text.Ui size="md" style={{ flex: 1 }}>
                    {`Balance: $${formatCentsToDollarString(displayBalance ?? 0)}`}
                  </Text.Ui>
                </View>
              }
              onPress={() => {
                logPaymentMethodSelected(logRBIEvent, 'Gift Card');
                setShowAddPaymentMethod(false);
                setSelectedGiftCardAccountId(accountId || undefined);
                onClose();
              }}
              selected={isSelected}
            />
          );
        })}

      {canUseGiftCards && (
        <PaymentEntry
          testID={PAYMENT_ENTRY_ADD_NEW_GIFT_CARD}
          icon={paymentUi.addNewGiftCard.icon}
          text={paymentUi.addNewGiftCard.label}
          onPress={() => {
            logPaymentMethodSelected(logRBIEvent, 'Add New Gift Card');
            onClose();
            onAddNewGiftCardPress?.();
          }}
          selected={false}
        />
      )}

      {!isUserAccountsPending && !showAddPaymentMethod && (
        <PaymentEntry
          testID={PAYMENT_ENTRY_ADD_NEW_CARD}
          onPress={() => {
            logAddNewPaymentCard(logRBIEvent);
            setSelectedPaymentMethod(null);
            setShowAddPaymentMethod(true);
            onClose();
          }}
          icon={paymentUi.addNewCard.icon}
          text={paymentUi.addNewCard.label}
          selected={selectedPaymentMethod === null}
        />
      )}
    </>
  );
}
